// Shared column resize logic used by both the standalone column-resize controller
// and the legacy checkbox-select-all controller.
//
// Call initColumnResize(rootElement) from any Stimulus controller's connect().
// rootElement should be an ancestor of the <table> whose columns are resizable.
//
// Each resizable <th> needs: data-column-resizable="<unique-key>"
// Optional: data-column-resize-default="<number>" to set an initial width (in px)
//           when no stored value exists. Without it, offsetWidth is used.
//
// Widths are persisted to localStorage under the "columnWidth" key.

const MIN_COLUMN_WIDTH = 40;
const MAX_COLUMN_WIDTH_RATIO = 0.8;

// A column wider than the visible area leaves the sticky first column covering the
// whole viewport, which makes horizontal scrolling look like it does nothing.
function maxColumnWidth(table) {
  const container = table.closest('.table-scroll') || table.parentElement;
  const containerWidth = container?.clientWidth || 0;
  const basis = containerWidth > 0 ? containerWidth : window.innerWidth;

  return Math.floor(basis * MAX_COLUMN_WIDTH_RATIO);
}

function clampColumnWidth(width, table) {
  return Math.min(Math.max(MIN_COLUMN_WIDTH, width), maxColumnWidth(table));
}

function declaredWidth(th) {
  return parseInt(th.style.width, 10) || MIN_COLUMN_WIDTH;
}

function fitConstrainedColumns(table, pinnedTh) {
  const headers = Array.from(table.querySelectorAll('thead tr:first-child th'));
  const container = table.closest('.table-scroll') || table.parentElement;
  const tableWidth = container?.clientWidth || window.innerWidth;
  let overflow = headers.reduce((total, th) => total + declaredWidth(th), 0) - tableWidth;
  if (overflow <= 0) return [];

  const shrinkable = headers.filter(th => th !== pinnedTh && th.style.width);
  const slack = shrinkable.reduce((total, th) => total + declaredWidth(th) - MIN_COLUMN_WIDTH, 0);
  const ratio = slack > 0 ? Math.min(1, overflow / slack) : 0;
  const resized = [];

  shrinkable.forEach(th => {
    const width = declaredWidth(th);
    const reduction = Math.min(width - MIN_COLUMN_WIDTH, Math.ceil((width - MIN_COLUMN_WIDTH) * ratio));
    if (reduction <= 0) return;

    th.style.width = (width - reduction) + 'px';
    overflow -= reduction;
    resized.push(th);
  });

  if (overflow > 0 && pinnedTh) {
    pinnedTh.style.width = Math.max(MIN_COLUMN_WIDTH, declaredWidth(pinnedTh) - overflow) + 'px';
  }

  return resized;
}

export function initColumnResize(rootElement) {
  var thElm;
  var startOffset;
  var isResizing = false;

  var columnWidth = {};
  if (localStorage.getItem('columnWidth')) {
    try {
      columnWidth = JSON.parse(localStorage.getItem('columnWidth'));
      Object.entries(columnWidth).forEach(([key, value]) => {
        if (!value.endsWith('px')) {
          columnWidth[key] = value + 'px';
        }
      });
    } catch (e) {
      console.error('Error loading stored column widths:', e);
      columnWidth = {};
    }
  }

  Array.prototype.forEach.call(
    rootElement.querySelectorAll("table thead th"),
    function (th) {
      // Idempotency guard: skip columns already initialized so reconnects /
      // overlapping controllers don't append duplicate grips or stack listeners.
      if (th.dataset.columnResizeInitialized === "true") return;
      th.dataset.columnResizeInitialized = "true";

      const table = th.closest('table');
      const columnIndex = th.cellIndex;

      if (table.closest('.table-scroll--non-resizable')) {
        const cells = table.querySelectorAll(`tr td:nth-child(${columnIndex + 1}), tr th:nth-child(${columnIndex + 1})`);
        cells.forEach(cell => {
          cell.style.width = '100%';
        });
        return;
      }

      const cells = table.querySelectorAll(`tr td:nth-child(${columnIndex + 1}), tr th:nth-child(${columnIndex + 1})`);
      cells.forEach(cell => {
        cell.style.overflow = 'hidden';
        cell.style.textOverflow = 'ellipsis';
        cell.style.whiteSpace = 'nowrap';
      });

      const constrained = Boolean(table.closest('.table-scroll--constrained'));

      if (constrained) {
        const storedWidth = columnWidth && columnWidth[th.dataset.columnResizable];
        const preferredWidth = storedWidth || th.dataset.columnResizeDefault;
        if (th.dataset.columnResizable && preferredWidth) {
          th.style.width = Math.max(MIN_COLUMN_WIDTH, parseInt(preferredWidth, 10)) + 'px';
        }
      } else if (columnWidth && columnWidth[th.dataset.columnResizable]) {
        // Clamped for display only. Overwriting the stored value would destroy a width
        // set on a wider page, since these keys are shared across tables.
        const storedWidth = parseInt(columnWidth[th.dataset.columnResizable], 10);
        const displayWidth = clampColumnWidth(storedWidth, table) + 'px';
        cells.forEach(cell => {
          cell.style.width = displayWidth;
          cell.style.minWidth = displayWidth;
          cell.style.maxWidth = displayWidth;
        });
      } else if (th.dataset.columnResizable) {
        const defaultWidth = th.dataset.columnResizeDefault
          ? parseInt(th.dataset.columnResizeDefault, 10)
          : th.offsetWidth;
        const initialWidth = clampColumnWidth(defaultWidth, table) + 'px';
        cells.forEach(cell => {
          cell.style.minWidth = initialWidth;
          cell.style.width = initialWidth;
          cell.style.maxWidth = initialWidth;
        });
        columnWidth[th.dataset.columnResizable] = initialWidth;
      }

      var grip = document.createElement('div');
      grip.classList.add('table__grip');
      grip.addEventListener('mousedown', function (e) {
        thElm = th;
        startOffset = th.offsetWidth - e.pageX;
        isResizing = true;

        e.preventDefault();
        document.body.style.cursor = 'col-resize';

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      });

      th.appendChild(grip);

      grip.addEventListener('touchstart', function (e) {
        if (e.touches.length === 1) {
          const touch = e.touches[0];
          thElm = th;
          startOffset = th.offsetWidth - touch.pageX;
          isResizing = true;

          document.addEventListener('touchmove', onTouchMove, { passive: false });
          document.addEventListener('touchend', onTouchEnd);

          e.preventDefault();
        }
      }, { passive: false });

      function onTouchMove(e) {
        if (isResizing && e.touches.length === 1) {
          applyDragWidth(e.touches[0].pageX);

          e.preventDefault();
        }
      }

      function onTouchEnd(e) {
        if (isResizing) {
          try {
            localStorage.setItem('columnWidth', JSON.stringify(columnWidth));
          } catch (err) {
            console.error('Error saving column widths:', err);
          }

          thElm = undefined;
          isResizing = false;

          document.removeEventListener('touchmove', onTouchMove);
          document.removeEventListener('touchend', onTouchEnd);
        }
      }
    });

  rootElement.querySelectorAll('.table-scroll--constrained table').forEach(table => {
    fitConstrainedColumns(table, null);
  });

  function onMouseMove(e) {
    if (thElm && isResizing) {
      e.preventDefault();

      applyDragWidth(e.pageX);
    }
  }

  function applyDragWidth(pageX) {
    const table = thElm.closest('table');
    const constrained = Boolean(table.closest('.table-scroll--constrained'));
    const dragWidth = startOffset + pageX;
    let width;

    if (constrained) {
      thElm.style.width = Math.max(MIN_COLUMN_WIDTH, dragWidth) + 'px';
      fitConstrainedColumns(table, thElm).forEach(th => {
        if (th.dataset.columnResizable) columnWidth[th.dataset.columnResizable] = th.style.width;
      });
      width = declaredWidth(thElm);
    } else {
      width = clampColumnWidth(dragWidth, table);
      const newWidth = width + 'px';
      const columnIndex = thElm.cellIndex;

      if (!thElm.resizableCells) {
        thElm.resizableCells = table.querySelectorAll(
          `tr td:nth-child(${columnIndex + 1}), tr th:nth-child(${columnIndex + 1})`
        );
      }

      thElm.resizableCells.forEach(cell => {
        cell.style.width = newWidth;
        cell.style.minWidth = newWidth;
        cell.style.maxWidth = newWidth;
      });
    }

    columnWidth[thElm.dataset.columnResizable] = width + 'px';
  }

  function onMouseUp() {
    if (thElm) {
      try {
        localStorage.setItem('columnWidth', JSON.stringify(columnWidth));
      } catch (e) {
        console.error('Error saving column widths:', e);
      }

      document.body.style.cursor = '';

      thElm = undefined;
      isResizing = false;

      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    }
  }
}
